import React, { useCallback, useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { ExpensesTable } from './ExpensesTable';
import { ExpenseSidebar } from './ExpenseSidebar/ExpenseSidebar';
import {
  useDeleteExpenseMutation,
  useExpensesQuery,
  useDeleteExpenseGroupMutation,
  useUpdateExpenseGroupStatusMutation,
  ExpenseStatus,
  ExpenseSortFieldEnum,
  AscDescEnum,
} from '../../__generated__/graphql';
import { Expense } from './types';
import { mapExpense } from './mappings';
import ServerError from '../../../../components/ServerError';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { routes, routes as mainRoutes } from '../../routes';
import {
  ExpenseDeleteConfirmation,
  ExpenseGroupDeleteConfirmation,
} from './DeleteConfirmations';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { ExpenseSaveSidebar } from './ExpenseSaveSidebar';
import { ExpenseGroupCreateModal } from './ExpenseGroupCreateModal';
import { Maybe } from '../../../../utils/maybe';
import { useHistory, useParams } from 'react-router-dom';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { useSelectedItemWithComments } from '../../components/Comment';
import { Item } from './useExpenseTableRenderSettings';
import { ExpenseGroupWithRelations } from './ExpensesTable/ExpensesTable';
import { buildExpenseQueryParams } from './queryParamsHelpers';

const MyExpenses: React.FC = () => {
  const history = useHistory();
  const { id: routeExpenseId } = useParams<{ id: string }>();
  const { defaultSelectedItem: selectedItemWithComments } =
    useSelectedItemWithComments();

  const { data, loading, error, refetch } = useExpensesQuery({
    variables: {
      sort: [
        { field: ExpenseSortFieldEnum.CreatedDate, order: AscDescEnum.Desc },
      ],
    },
  });
  const [deleteExpense, { loading: deleteInProgress }] =
    useDeleteExpenseMutation();
  const [deleteExpenseGroup, { loading: deleteExpenseGroupInProgress }] =
    useDeleteExpenseGroupMutation();
  const [updateExpenseGroupStatus] = useUpdateExpenseGroupStatusMutation();
  const errorHandler = useMutationErrorHandler();

  const { t } = useTranslation();

  useFlairBreadcrumbHook([
    { label: t('navigation.menuItems.profile.title') },
    { label: t('expenses.title') },
  ]);

  const { addSuccess, addError } = useToasts();

  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [deleteExpenseGroupId, setDeleteExpenseGroupId] = useState<
    string | null
  >(null);
  const [deleteExpenseGroupInfo, setDeleteExpenseGroupInfo] = useState<{
    id: string;
    name: string;
    childCount: number;
  } | null>(null);
  const [submittingExpenseGroupId, setSubmittingExpenseGroupId] = useState<
    string | null
  >(null);
  const [saveSideBarVisible, setSaveSideBarVisible] = useState<boolean>(false);
  const [prefilledExpenseGroupId, setPrefilledExpenseGroupId] = useState<
    string | undefined
  >(undefined);
  const [createExpenseGroupModalVisible, setCreateExpenseGroupModalVisible] =
    useState<boolean>(false);
  const [expenseFormRefreshTrigger, setExpenseFormRefreshTrigger] =
    useState<number>(0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const expandedParam = urlParams.get('expanded');
    return expandedParam
      ? new Set(expandedParam.split(',').filter(Boolean))
      : new Set();
  });

  const handleExpandedGroupsChange = useCallback(
    (groups: Set<string>) => {
      setExpandedGroups(groups);

      const currentQueryParams = Object.fromEntries(
        new URLSearchParams(window.location.search),
      );

      const allQueryParams = buildExpenseQueryParams({
        item: null,
        expandedGroups: groups,
        currentQueryParams,
      });

      history.replace(
        routes.myExpenses.route.withQueryParams(allQueryParams).create({}),
      );
    },
    [history],
  );

  useEffect(() => {
    if (!loading && data && window.location.hash) {
      const targetId = window.location.hash.substring(1);

      const scrollToElement = () => {
        const element = document.getElementById(targetId);
        if (element) {
          const elementTop =
            element.getBoundingClientRect().top + window.pageYOffset;
          const offsetTop = elementTop - 4 * 16;

          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
          return true;
        }
        return false;
      };

      const attemptScroll = () => {
        if (!scrollToElement()) {
          setTimeout(attemptScroll, 100);
        }
      };

      setTimeout(attemptScroll, 300);
    }
  }, [loading, data]);

  const expenseDelete = useCallback(async () => {
    if (deleteItemId === null) {
      return;
    }
    await deleteExpense({
      variables: {
        id: deleteItemId,
      },
      update: (cache, { data }) => {
        if (!data?.expenses?.deleteExpense?.query?.me) return;

        const newExpenses = data.expenses.deleteExpense.query.me.myExpenses;

        cache.modify({
          fields: {
            me: (existingMe, { readField }) => {
              const meId = readField('Id', existingMe);
              const meCacheId = cache.identify({ __typename: 'Me', Id: meId });

              if (meCacheId) {
                cache.modify({
                  id: meCacheId,
                  fields: {
                    myExpenses: () => newExpenses,
                  },
                });
              }

              return existingMe;
            },
          },
        });
      },
    })
      .then((r) => {
        if (r.data?.expenses.deleteExpense.error) {
          addError(r.data?.expenses.deleteExpense.error);
          return;
        }
        addSuccess(t('expenses.expenseDeletedToast'));
      })
      .catch(errorHandler)
      .finally(() => {
        setDeleteItemId(null);
      });
  }, [addError, addSuccess, deleteExpense, deleteItemId, errorHandler, t]);

  const handleCreateBtnClick = useCallback(() => {
    setSaveSideBarVisible(true);
  }, []);

  const handleSaveSidebarClose = useCallback(
    (groupId?: string) => {
      setSaveSideBarVisible(false);
      setPrefilledExpenseGroupId(undefined);
      refetch();

      if (groupId) {
        setExpandedGroups((prev) => {
          const newExpandedGroups = new Set([...prev, groupId]);

          const currentQueryParams = Object.fromEntries(
            new URLSearchParams(window.location.search),
          );
          const allQueryParams = buildExpenseQueryParams({
            item: null,
            expandedGroups: newExpandedGroups,
            currentQueryParams,
          });

          const newUrl = routes.myExpenses.route
            .withQueryParams(allQueryParams)
            .create({});
          history.replace(`${newUrl}#${groupId}`);

          return newExpandedGroups;
        });
      }
    },
    [refetch, history],
  );

  const handleAddExpense = useCallback((expenseGroupId: string) => {
    setPrefilledExpenseGroupId(expenseGroupId);
    setSaveSideBarVisible(true);
  }, []);

  const handleCreateExpenseGroupBtnClick = useCallback(() => {
    setCreateExpenseGroupModalVisible(true);
  }, []);

  const handleCreateExpenseGroupModalClose = useCallback(() => {
    setCreateExpenseGroupModalVisible(false);
  }, []);

  const handleCreateExpenseGroupModalSave = useCallback(
    (createdExpenseGroupId?: string) => {
      setCreateExpenseGroupModalVisible(false);
      setExpenseFormRefreshTrigger((prev) => prev + 1);

      if (createdExpenseGroupId) {
        setExpandedGroups((prev) => {
          const newExpandedGroups = new Set([...prev, createdExpenseGroupId]);

          const currentQueryParams = Object.fromEntries(
            new URLSearchParams(window.location.search),
          );
          const allQueryParams = buildExpenseQueryParams({
            item: null,
            expandedGroups: newExpandedGroups,
            currentQueryParams,
          });

          const newUrl = routes.myExpenses.route
            .withQueryParams(allQueryParams)
            .create({});
          history.replace(`${newUrl}#${createdExpenseGroupId}`);

          return newExpandedGroups;
        });
      }
    },
    [history],
  );

  const handleSubmitExpenseGroup = useCallback(
    async (expenseGroupId: string) => {
      try {
        setSubmittingExpenseGroupId(expenseGroupId);
        await updateExpenseGroupStatus({
          variables: {
            input: {
              id: expenseGroupId,
              status: ExpenseStatus.Pending,
              declineReason: null,
              reimbursed: null,
            },
          },
        });
        addSuccess(t('expenses.expenseGroupSubmittedToast'));
      } catch (error) {
        errorHandler(error);
      } finally {
        setSubmittingExpenseGroupId(null);
      }
    },
    [addSuccess, t, errorHandler, updateExpenseGroupStatus],
  );

  const expenseGroupDelete = useCallback(
    async (idToDelete?: string) => {
      const targetId =
        idToDelete || deleteExpenseGroupId || deleteExpenseGroupInfo?.id;
      if (!targetId) {
        return;
      }
      await deleteExpenseGroup({
        variables: {
          id: targetId,
        },
        update: (cache, { data }) => {
          if (!data?.expenseGroups?.deleteExpenseGroup?.query?.me) return;

          const newExpenseGroups =
            data.expenseGroups.deleteExpenseGroup.query.me.myExpenseGroups;
          const newExpenses =
            data.expenseGroups.deleteExpenseGroup.query.me.myExpenses;

          cache.modify({
            fields: {
              me: (existingMe, { readField }) => {
                const meId = readField('Id', existingMe);
                const meCacheId = cache.identify({
                  __typename: 'Me',
                  Id: meId,
                });

                if (meCacheId) {
                  cache.modify({
                    id: meCacheId,
                    fields: {
                      myExpenseGroups: () => newExpenseGroups,
                      myExpenses: () => newExpenses,
                    },
                  });
                }

                return existingMe;
              },
            },
          });
        },
      })
        .then((r: any) => {
          if (r.data?.expenseGroups.deleteExpenseGroup.error) {
            addError(r.data?.expenseGroups.deleteExpenseGroup.error);
            return;
          }
          addSuccess(t('expenses.expenseGroupDeletedToast'));
        })
        .catch(errorHandler)
        .finally(() => {
          setDeleteExpenseGroupId(null);
          setDeleteExpenseGroupInfo(null);
        });
    },
    [
      addError,
      addSuccess,
      deleteExpenseGroup,
      deleteExpenseGroupId,
      deleteExpenseGroupInfo?.id,
      errorHandler,
      t,
    ],
  );

  const handleDeleteExpenseGroup = useCallback(
    (
      expenseGroupId: string,
      expenseGroupName: string,
      childExpenseCount: number,
    ) => {
      if (childExpenseCount === 0) {
        setDeleteExpenseGroupId(expenseGroupId);
        expenseGroupDelete(expenseGroupId);
      } else {
        setDeleteExpenseGroupInfo({
          id: expenseGroupId,
          name: expenseGroupName,
          childCount: childExpenseCount,
        });
      }
    },
    [expenseGroupDelete],
  );

  const handleDeleteExpenseGroupClose = () => {
    setDeleteExpenseGroupId(null);
    setDeleteExpenseGroupInfo(null);
  };

  if (error) {
    return <ServerError />;
  }

  const handleDelete = (expenseId: string) => {
    setDeleteItemId(expenseId);
  };

  const handleDeleteClose = () => {
    setDeleteItemId(null);
  };

  let allExpenses: Expense[] = data?.me.myExpenses.map(mapExpense) || [];
  let allExpenseGroups: ExpenseGroupWithRelations[] = [
    ...(data?.me.myExpenseGroups ?? []),
  ];

  const handleOnItemClick = (item: Maybe<Item>) => {
    const currentQueryParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    const allQueryParams = buildExpenseQueryParams({
      item,
      expandedGroups,
      currentQueryParams,
    });

    if (item?.id) {
      history.push(
        routes.myExpense.route
          .withQueryParams(allQueryParams)
          .create({ id: item.id }),
      );
    } else {
      history.push(
        routes.myExpenses.route.withQueryParams(allQueryParams).create({}),
      );
    }
  };

  const renderContent = () => {
    if (loading || !data) {
      return <Loading />;
    }

    return (
      <ExpensesTable
        onItemClick={handleOnItemClick}
        expenses={allExpenses}
        expenseGroups={allExpenseGroups}
        onDelete={handleDelete}
        onDeleteExpenseGroup={handleDeleteExpenseGroup}
        deletingExpenseGroupId={
          deleteExpenseGroupId || deleteExpenseGroupInfo?.id
        }
        submittingExpenseGroupId={submittingExpenseGroupId || undefined}
        isMyExpenses={true}
        filterStorageKey="my_expense_filter"
        createRouteUrl={(queryParams) =>
          mainRoutes.myExpenses.route.withQueryParams(queryParams).create({})
        }
        onCreateBtnClick={handleCreateBtnClick}
        onCreateExpenseGroupBtnClick={handleCreateExpenseGroupBtnClick}
        onAddExpense={handleAddExpense}
        onSubmitExpenseGroup={handleSubmitExpenseGroup}
        expandedGroups={expandedGroups}
        onExpandedGroupsChange={handleExpandedGroupsChange}
      />
    );
  };
  const selectedItemId = routeExpenseId ?? selectedItemWithComments?.id;
  const showComments = selectedItemWithComments?.showComments ?? false;
  const urlParams = new URLSearchParams(window.location.search);
  const showItems = urlParams.get('showItems') === 'true';
  let item = allExpenses?.find((x) => x.id === selectedItemId) ?? null;
  return (
    <>
      <PageHeader title={t('expenses.title')} />
      {renderContent()}
      {item && (
        <ExpenseSidebar
          isManager={false}
          expense={item}
          onClose={() => handleOnItemClick(null)}
          showComments={showComments}
          showItems={showItems}
        />
      )}
      <ExpenseDeleteConfirmation
        visible={!!deleteItemId}
        submitInProgress={deleteInProgress}
        onClose={handleDeleteClose}
        onSubmit={expenseDelete}
      />
      <ExpenseGroupDeleteConfirmation
        visible={!!deleteExpenseGroupInfo}
        submitInProgress={deleteExpenseGroupInProgress}
        onClose={handleDeleteExpenseGroupClose}
        onSubmit={() => expenseGroupDelete()}
        expenseGroupName={deleteExpenseGroupInfo?.name}
        childExpenseCount={deleteExpenseGroupInfo?.childCount}
      />
      <ExpenseSaveSidebar
        show={saveSideBarVisible}
        onSave={handleSaveSidebarClose}
        onClose={handleSaveSidebarClose}
        expenseGroupId={prefilledExpenseGroupId}
        refreshTrigger={expenseFormRefreshTrigger}
      />
      <ExpenseGroupCreateModal
        show={createExpenseGroupModalVisible}
        onClose={handleCreateExpenseGroupModalClose}
        onSave={handleCreateExpenseGroupModalSave}
      />
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={5} rows={5} />;
};

export default MyExpenses;
